import { LayoutContext } from 'app/components/ReactContexts/layoutContext';
import { useTrackRecentModel } from 'app/hooks/resources/useTrackRecentModel';
import { useImageUrlWithPlaceholder } from 'app/hooks/useImageUrlWithPlaceholder';
import { ic_nav_back as BackIcon } from 'app/widgets/SVGs';
import ChevronRightIcon from 'assets/images/chevron_right.svg';
import CompatibleAttachmentsIcon from 'assets/images/ic_compatible_attachments.svg';
import CompatibleMachinesIcon from 'assets/images/ic_res_compatible.svg';
import FaultCodeIcon from 'assets/images/ic_res_fault_code-2.svg';
import MaintenanceScheduleIcon from 'assets/images/ic_res_maint_schedule.svg';
import GuidesIcon from 'assets/images/ic_res_maintenance_guides.svg';
import ManualsIcon from 'assets/images/ic_res_manuals.svg';
import VideoResourcesIcon from 'assets/images/ic_res_video.svg';
import WarrantyInformationIcon from 'assets/images/ic_res_warranty_info.svg';
import PlaceHolderModel from 'assets/images/ph_resources_model_header.png';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createSearchParams, useLocation, useNavigate } from 'react-router-dom';
import {
  selectCanonicalSlugPathByCategoryId,
  selectCompatibleIdsByMachineId,
  selectModelById,
} from 'services/api/resources/selectors';
import { RootState, useAppSelector } from 'services/store';
import './ModelPage.less';
import { MODEL_SEPARATOR } from '../constants';

interface Props {
  modelId: string;
  categoryPath: string;
}

export default function V2ModelPage({ modelId, categoryPath }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  useTrackRecentModel(modelId);

  const model = useAppSelector((state: RootState) =>
    selectModelById(state, modelId),
  );

  const expectedCategoryPath = useAppSelector((state: RootState) =>
    model ? selectCanonicalSlugPathByCategoryId(state, model.categoryId) : null,
  );

  const modelUrl = useImageUrlWithPlaceholder(
    model?.modelHeroUrl,
    PlaceHolderModel,
  );

  const compatibleIds = useAppSelector(state =>
    selectCompatibleIdsByMachineId(state, modelId),
  );

  const isAttachmentType = model?.type === 'attachment';

  useEffect(() => {
    if (!model || !expectedCategoryPath) return;

    if (categoryPath !== expectedCategoryPath) {
      console.log(
        `Incorrect path: "${categoryPath}". Redirecting to canonical: "${expectedCategoryPath}"`,
      );

      const canonicalPathString = `${expectedCategoryPath}${MODEL_SEPARATOR}${modelId}`;

      navigate(
        {
          pathname: `/resources/${canonicalPathString}`,
          search: location.search,
        },
        { replace: true },
      );
    }
  }, [
    model,
    expectedCategoryPath,
    categoryPath,
    modelId,
    navigate,
    location.search,
  ]);

  const { setClassNames, removeClassNames } = React.useContext(LayoutContext);
  useEffect(() => {
    let index: number = setClassNames('layout-grey');
    return () => removeClassNames(index);
  }, []);

  const getCompatibilityLink = () => {
    return {
      pathname: '/resources',
      search: createSearchParams({
        compatibleWith: modelId,
      }).toString(),
    };
  };

  if (!model)
    return (
      <div className="text-center">
        {t('resources.errors.no_resources_found')}
      </div>
    );

  return (
    <div className="model-page relative">
      <span
        className="model-page__back-button back-button back-button-on-layout fixed-on-desktop"
        onClick={() => {
          navigate(-1);
        }}
      >
        <BackIcon />
      </span>
      <div className="model-title-box contrast-enhanced-img">
        <span
          className="image-enh"
          style={{ backgroundImage: `url(${modelUrl})` }}
        ></span>
        <div className="model-title">
          <h2>{model.model}</h2>
          {model.modelDescription && (
            <div className="model-description">{model.modelDescription}</div>
          )}
        </div>
      </div>

      <div className="row section-margin-top">
        <div className="col-sm-12">
          <div className="equipment-content-actions-title">
            <h4>{t('title_resources')}</h4>
          </div>
          <div className="arrow-links arrow-links-no-icon-border">
            {model.hasFaultCodes && (
              <div
                className="arrow-links_item"
                onClick={() => {
                  navigate('/faultCodes/' + model.model);
                }}
              >
                <img className="arrow-links_icon" src={FaultCodeIcon}></img>
                <span>{t('fault_code_lookup')}</span>
                <img
                  className="arrow-links_icon arrow-links_icon-right"
                  src={ChevronRightIcon}
                />
              </div>
            )}
            {model.manualEntries && model.manualEntries.length > 0 && (
              <div
                className="arrow-links_item"
                onClick={() => navigate('/manual/' + model.model)}
              >
                <img className="arrow-links_icon" src={ManualsIcon}></img>
                <span>{t('manuals')}</span>
                <img
                  className="arrow-links_icon arrow-links_icon-right"
                  src={ChevronRightIcon}
                />
              </div>
            )}
            {model.guideUrl && (
              <div
                className="arrow-links_item"
                onClick={() => navigate('/guides/' + model.model)}
              >
                <img className="arrow-links_icon" src={GuidesIcon}></img>
                <span>{t('maintenance_guides')}</span>
                <img
                  className="arrow-links_icon arrow-links_icon-right"
                  src={ChevronRightIcon}
                />
              </div>
            )}
            {model.hasMaintenanceSchedules && (
              <div
                className="arrow-links_item"
                onClick={() =>
                  navigate(location.pathname + '/maintenanceSchedule')
                }
              >
                <img
                  className="arrow-links_icon"
                  src={MaintenanceScheduleIcon}
                />
                <span>{t('maintenance_schedules')}</span>
                <img
                  className="arrow-links_icon arrow-links_icon-right"
                  src={ChevronRightIcon}
                />
              </div>
            )}

            {!!compatibleIds?.length && (
              <div
                className="arrow-links_item"
                onClick={() => navigate(getCompatibilityLink())}
              >
                <img
                  className="arrow-links_icon"
                  src={
                    isAttachmentType
                      ? CompatibleMachinesIcon
                      : CompatibleAttachmentsIcon
                  }
                />
                <span>
                  {t(
                    isAttachmentType
                      ? 'compatible_machines'
                      : 'compatible_attachments',
                  )}
                </span>
                <img
                  className="arrow-links_icon arrow-links_icon-right"
                  src={ChevronRightIcon}
                />
              </div>
            )}

            {model.videoEntries && model.videoEntries.length > 0 && (
              <div
                className="arrow-links_item"
                onClick={() => navigate('/videoResources/' + model.model)}
              >
                <img className="arrow-links_icon" src={VideoResourcesIcon} />
                <span>{t('instructional_videos_title')}</span>
                <img
                  className="arrow-links_icon arrow-links_icon-right"
                  src={ChevronRightIcon}
                />
              </div>
            )}

            {model.warrantyUrl && model.type == 'machine' && (
              <div
                className="arrow-links_item"
                onClick={() => {
                  window.open(model.warrantyUrl, '_blank');
                }}
              >
                <img
                  className="arrow-links_icon"
                  src={WarrantyInformationIcon}
                />
                <span>{t('warranty_info')}</span>
                <img
                  className="arrow-links_icon arrow-links_icon-right"
                  src={ChevronRightIcon}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
